import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";

export interface IdExtractionData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export function useUserIdExtraction(): IdExtractionData | null {
  const { person } = useAppContext();
  const estateId = person?.estate_id;

  const { data: extraction } = useQuery({
    queryKey: ["userIdExtraction", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_documents")
        .select("specific_doc_extraction")
        .eq("estate_id", estateId!)
        .eq("document_type", "identification_card")
        .not("specific_doc_extraction", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data?.specific_doc_extraction as Record<string, unknown> | null;
    },
    enabled: !!estateId,
  });

  return useMemo(() => {
    if (!extraction) return null;

    const str = (key: string): string => {
      const val = extraction[key];
      if (typeof val !== "string") return "";
      const trimmed = val.trim();
      if (
        /^<.*>$/.test(trimmed) ||
        trimmed.toUpperCase() === "UNKNOWN" ||
        trimmed.toUpperCase() === "N/A"
      )
        return "";
      return trimmed;
    };

    const firstName = str("first_name");
    const lastName = str("last_name");
    const address = str("address");
    const city = str("city");
    const state = str("state");
    const zip = str("zip");

    if (!address && !city && !state) return null;

    return { firstName, lastName, address, city, state, zip };
  }, [extraction]);
}
